import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { getPredictionMarketEventsFindByIdQueryKey, usePredictionMarketEventsFindById } from '@/lib/endpoints';
import { useTradingRealtimeSync } from '@/lib/trading/useTradingRealtimeSync';
import { useSettlementResync } from '@/lib/trading/useSettlementResync';
import { isMarketSettled, isOutcomeSettled, useMarketRealtime } from '@/lib/realtime';
import { MarketPageHeader } from './MarketPageHeader';
import { MarketChartContainer } from './MarketChartContainer';
import { MarketBuyEntry } from './MarketBuyEntry';
import { MarketSections } from './MarketSections';
import { isEventClosed, resultMarketsOf } from './market-status';
import { BinaryMarketPageSkeleton } from './BinaryMarketPageSkeleton';
import { BuyInPopup } from './BuyInPopup';
import { useBuyTarget } from './useBuyTarget';
import { SportsMarketDetail } from '../SportsMarkets/SportsMarketDetail';

/** Re-fetch delays after a settlement signal — covers Plaee's settle lag (mirrors useSettlementResync). */
const EVENT_REFRESH_DELAYS_MS = [3000, 6000] as const;

/**
 * Null-rendering per-market settlement subscriber (a hook can't loop over
 * markets). Besides the trading-mirror resync, a settlement also refetches the
 * event detail so the page flips to its closed state (buy/sell removed,
 * Result shown) without a reload.
 */
function SettlementResync({
  eventId,
  marketId,
  externalMarketId,
}: {
  eventId: string;
  marketId: string;
  externalMarketId: string;
}) {
  useSettlementResync(marketId, externalMarketId);

  const queryClient = useQueryClient();
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    },
    [],
  );
  function refreshEvent() {
    const invalidate = () =>
      void queryClient.invalidateQueries({ queryKey: getPredictionMarketEventsFindByIdQueryKey(eventId) });
    timersRef.current.forEach(clearTimeout);
    invalidate();
    timersRef.current = EVENT_REFRESH_DELAYS_MS.map((delay) => setTimeout(invalidate, delay));
  }
  useMarketRealtime(marketId, externalMarketId, {
    onOutcomeUpdated: (e) => {
      if (isOutcomeSettled(e)) refreshEvent();
    },
    onMarketUpdated: (e) => {
      if (isMarketSettled(e)) refreshEvent();
    },
  });
  return null;
}

/**
 * Binary-question market page (ARC-82 §5).
 *
 * Renders straight from the backend-resolved market shape (`renderKind` +
 * `options`): header (§5.2), price chart (§5.2), buy entries (§5.3), and the
 * admin-ordered sections (§5.4). A BINARY market shows a Yes/No bar; a MULTI
 * market shows one card per candidate; a multi-market event stacks its markets.
 */
export function BinaryMarketPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { data: event, isLoading, isError } = usePredictionMarketEventsFindById(eventId ?? '');
  const { market: buyContext, initialSide, onOpenBuy, close } = useBuyTarget(event?.name ?? '');

  // Fills/sells/cancels via the private bus; per-market settlement via <SettlementResync> below.
  useTradingRealtimeSync();

  // A single BINARY market shows just one Yes/No pair — pin it to the bottom of
  // the viewport above the footer/bottom-nav. MULTI and multi-market events keep
  // their buy entries inline, where each market needs its own labelled bar.
  const isSingleBinary = !!event && event.markets.length === 1 && event.markets[0]?.renderKind === 'BINARY';

  // Detect when the pinned bar is actually "stuck" vs. resting at the bottom of
  // the content: the sentinel sits just below the bar, so once it scrolls into
  // view (minus the bottom-nav height) the bar has reached its natural position.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
      rootMargin: '0px 0px -82px 0px',
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSingleBinary]);

  if (isLoading) return <BinaryMarketPageSkeleton />;
  if (isError || !event) {
    return <p className="text-sm text-muted py-4 text-center">Failed to load this market.</p>;
  }

  // A SPORTS match event (moneyline present) renders the sports layout instead of
  // the binary one; winner/futures/prop-only sports events fall through to MULTI.
  // Gate on the moneyline, not just `sports`, so a sports block without one still
  // degrades to the MULTI layout rather than an empty sports page.
  if (event.sports?.moneyline) {
    return (
      <div className="flex flex-col gap-5">
        {event.markets.map((m) => (
          <SettlementResync key={m.id} eventId={event.id} marketId={m.id} externalMarketId={m.externalMarketId} />
        ))}
        <SportsMarketDetail event={event} sports={event.sports} />
      </div>
    );
  }

  const options = event.markets.flatMap((m) => m.options);

  // Closed market (= resolved, PRD §7.1): buy entries are removed entirely and
  // the sections swap to the closed set (Result in, trading sections out).
  const closed = isEventClosed(event.markets);

  // A closed SPORTS event narrows Result (and the frozen chart it feeds) to the
  // moneyline — the match outcome, not every prop market's winners.
  const resultMarkets = closed ? resultMarketsOf(event.eventType, event.markets) : event.markets;

  // Settled outcomes carry no tradeable options, so the frozen chart on a
  // closed page draws its lines from the Result rows instead. Open events rank
  // options by chance across ALL markets (flatMap only preserves favourites-first
  // order within each market) so the chart's top-N cap keeps the true leaders.
  const chartOutcomes = closed
    ? resultMarkets.flatMap((m) => m.results.map((r) => ({ id: r.outcomeId, outcomeId: r.outcomeId })))
    : [...options]
        .sort((a, b) => (b.chancePercent ?? -1) - (a.chancePercent ?? -1))
        .map((o) => ({ id: o.id, outcomeId: o.yes.outcomeId, label: o.label, currentPriceCents: o.yes.priceCents }));

  const buyEntry = closed ? null : <MarketBuyEntry eventId={event.id} markets={event.markets} onOpenBuy={onOpenBuy} />;

  return (
    <div className="flex flex-col gap-5">
      {event.markets.map((m) => (
        <SettlementResync key={m.id} eventId={event.id} marketId={m.id} externalMarketId={m.externalMarketId} />
      ))}
      <MarketPageHeader event={event} resolved={closed} />
      <MarketChartContainer outcomes={chartOutcomes} />
      {!isSingleBinary && buyEntry}
      <MarketSections event={event} options={options} resultMarkets={resultMarkets} />

      {isSingleBinary && buyEntry && (
        <>
          <div
            className={cn(
              // Mobile: pinned above the bottom-nav (82px + iOS safe area).
              'sticky z-40 bottom-[calc(82px+env(safe-area-inset-bottom))]',
              'border-border bg-surface/95 px-4 py-3 shadow-card backdrop-blur-sm',
              // While stuck, dock it: break out to the full width with a top border.
              // Once it settles at the bottom, show it as an inset rounded card.
              isStuck ? '-mx-4 border-t' : 'rounded-card border',
              // md+: the nav is a left sidebar, not a bottom bar — drop the sticky /
              // full-bleed treatment and render the buy entry inline as it was before.
              'md:static md:bottom-auto md:z-auto md:mx-0 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:backdrop-blur-none',
            )}
          >
            {buyEntry}
          </div>
          {/* Marks the bar's resting position so the observer can tell "stuck" from "settled". */}
          <div ref={sentinelRef} aria-hidden className="h-px" />
        </>
      )}

      <BuyInPopup market={buyContext} initialSide={initialSide} surface="event.detail" onClose={close} />
    </div>
  );
}
